import { AssetUploadType } from "@/constants/project";
import { RestApiResponse } from "@/types/common.api";
import { Model } from "@/types/models";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { MIME } from "@/constants/asset-type";

import type {
  EditorMedia,
  EditorAllMedia,
  EditorMediaParams,
  EditorMediaAudio,
  EditorMediaVideo,
  EditorMediaImage,
  EditorMediaFont,
  EditorMediaLogo,
} from "@/types/object";

type UploadMediaRequest = {
  uploadType?: AssetUploadType;
  file: File[] | File;
  workspaceId?: string;
}

type UploadMediaResponse = EditorMedia[];

export type GetBrandKitsResponse = {
  logos: EditorMediaLogo[];
  fonts: EditorMediaFont[];
  images: EditorMediaImage[];
  videos: EditorMediaVideo[];
  audios: EditorMediaAudio[];
};

type DownloadAssetRequest = {
  id: number | string;
  type: string;
  provider: string;
  user?: string;
}

type DownloadAssetResponse = {
  replaceUrl: string;
  stillUrl?: string;
  licensedUrl?: string;
  width?: number;
  height?: number;
}

const objectApi = createApi({
  reducerPath: "editor_object",
  refetchOnFocus: true,
  baseQuery: fetchBaseQuery({ baseUrl: "/api/project" }),
  tagTypes: ["media", "assets", "brandKit"],
  endpoints: (builder) => ({
    // media
    getAllMedia: builder.query<EditorAllMedia | null, void>({
      query: () => "/media/all",
      transformResponse: async (res: RestApiResponse<EditorAllMedia>) => {
        if (!res.success) {
          return null;
        }
        return res.data;
      },
    }),
    getAudios: builder.query<EditorMediaAudio[] | null, EditorMediaParams>({
      query: (args) => `/media/audios?search=${args.search}&provider=${args.provider}`,
      transformResponse: async (res: RestApiResponse<EditorMediaAudio[]>) => {
        if (!res.success || !res.data) {
          return null;
        }
        return res.data;
      },
    }),
    searchAudios: builder.mutation<EditorMediaAudio[], EditorMediaParams>({
      query: ({ search, provider }) => ({
        url: `/media/audios`,
        method: "POST",
        body: { search, provider },
      }),
      transformResponse: async (res: RestApiResponse<EditorMediaAudio[]>) => {
        if (!res.success || !res.data) {
          return [];
        }
        return res.data;
      },
    }),
    getVideos: builder.query<EditorMediaVideo[] | null, EditorMediaParams>({
      query: (args) => `/media/videos?search=${args.search}&provider=${args.provider}`,
      transformResponse: async (res: RestApiResponse<EditorMediaVideo[]>) => {
        if (!res.success || !res.data) {
          return null;
        }
        return res.data;
      },
    }),
    searchVideos: builder.mutation<EditorMediaVideo[], EditorMediaParams>({
      query: ({ search, provider }) => ({
        url: `/media/videos`,
        method: 'POST',
        body: { search, provider },
      }),
      transformResponse: async (res: RestApiResponse<EditorMediaVideo[]>) => {
        if (!res.success) {
          return [];
        }
        return res.data;
      },
    }),
    getImages: builder.query<EditorMediaImage[] | null, EditorMediaParams>({
      query: (args) => `/media/images?search=${args.search}&provider=${args.provider}`,
      transformResponse: async (res: RestApiResponse<EditorMediaImage[]>) => {
        if (!res.success || !res.data) {
          return null;
        }
        return res.data;
      },
    }),
    searchImages: builder.mutation<EditorMediaImage[], EditorMediaParams>({
      query: ({ search, provider }) => ({
        url: `/media/images`,
        method: "POST",
        body: { search, provider },
      }),
      transformResponse: async (res: RestApiResponse<EditorMediaImage[]>) => {
        if (!res.success) {
          return [];
        }
        return res.data;
      },
    }),

    /**
     * 애셋 업로드
     * 
     * uploadType을 지정하지 않으면 일반 애셋 업로드\
     * uploadType을 AUDIO_SCRIPT로 지정하면 오디오 스크립트 업로드
     */
    uploadMedia: builder.mutation<RestApiResponse<UploadMediaResponse>, UploadMediaRequest>({
      query: (uploadingData) => {
        const params = uploadingData.uploadType
          ? { uploadType: uploadingData.uploadType }
          : undefined;

        const files = Array.isArray(uploadingData.file)
          ? uploadingData.file
          : [uploadingData.file];

        const body = new FormData();

        files.forEach((file) => {
          if (MIME.image.includes(file.type)) {
            body.append("image", file);
          }
          if (MIME.audio.includes(file.type)) {
            body.append("audio", file);
          }
          if (MIME.video.includes(file.type)) {
            body.append("video", file);
          }
          if (MIME.font.includes(file.type)) {
            body.append("font", file);
          }
        });

        if (uploadingData.workspaceId) {
          body.append("workspaceId", uploadingData.workspaceId);
        }

        return {
          url: "/assets/upload",
          method: "POST",
          params,
          body,
          formData: true,
        };
      },
      invalidatesTags: [{ type: "assets" }],
      // transformResponse: async (res: RestApiResponse<UploadMediaResponse>) => {
      //   if (!res.success || !res.data) {
      //     return [];
      //   }
      //   return res.data;
      // },
    }),
    getAllAsset: builder.query<EditorAllMedia, void>({
      query: () => "/assets",
      transformResponse: async (res: RestApiResponse<EditorAllMedia>) => {
        if (!res.success || !res.data) {
          return {
            images: [],
            videos: [],
            audios: [],
          };
        }
        return res.data;
      },
      providesTags: () => [{ type: "assets" }],
    }),
    deleteAsset: builder.mutation<RestApiResponse<void>, { type: string; key: string, author?: string }>({
      query(args) {
        return {
          url: "/assets",
          method: "POST",
          body: args,
        };
      },
      invalidatesTags: [{ type: "assets" }],
    }),

    // find models
    getModels: builder.query<Model[], { languageCode: string, workspaceId?: string}>({
      query: ({languageCode = "en", workspaceId=''}) => `models?languageCode=${languageCode}&workspaceId=${workspaceId}`,
      transformResponse: async (res: RestApiResponse<Model[]>) => {
        if (!res.success || !res.data) {
          return [];
        }
        return res.data;
      },
    }),
    getAiGeneratedAvatars: builder.query<Model[], { workspaceId?: string}>({
      query: ({ workspaceId = '' }) => `models/ai-generated?workspaceId=${workspaceId}`,
      transformResponse: async (res: RestApiResponse<Model[]>) => {
        if (!res.success || !res.data) {
          return [];
        }
        return res.data;
      },
    }),
    getBrandKit: builder.query<GetBrandKitsResponse | null, { workspaceId: string }>({
      query: ({ workspaceId }) => `/brandKit?workspaceId=${workspaceId}`,
      transformResponse: (res: RestApiResponse<GetBrandKitsResponse>) => {
        if (!res.success || !res.data) {
          return null;
        }
        return res.data;
      },
      providesTags: () => [{ type: "assets" }],
    }),
    downloadAsset: builder.mutation<RestApiResponse<DownloadAssetResponse>, DownloadAssetRequest>({
      query: (args) => {
        return {
          url: "/media/download",
          method: "PUT",
          body: args,
        };
      },
    }),
  }),
});



export const {
  useGetAllMediaQuery,
  useGetAudiosQuery,
  useSearchAudiosMutation,
  useGetVideosQuery,
  useSearchVideosMutation,
  useGetImagesQuery,
  useSearchImagesMutation,
  useUploadMediaMutation,
  useGetAllAssetQuery,
  useDeleteAssetMutation,
  useGetModelsQuery,
  useGetAiGeneratedAvatarsQuery,
  useGetBrandKitQuery,
  useDownloadAssetMutation,
} = objectApi;

export default objectApi;
